import { PrivateURLConstants } from '@constants/private-url.constant';
import {
  getNextPageParam,
  useReactQueryCaching,
} from '@hooks/use-react-query-caching';
import {
  VERSIONS_INFINITE_QUERY_KEY,
  VERSIONS_QUERY_KEY,
} from '@modules/project-switcher/constants';
 
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';
import api from '../api/instance';
import { ApplicationVersion } from '../models/application-version.model';
import { Page } from '../models/page';

const nameSpace = PrivateURLConstants.VERSIONS;

const findAll = (
  filterQuery?: string,
  page?: number,
  sort?: string,
  size?: number,
) => {
  return api
    .get(nameSpace, {
      query: filterQuery || '',
      page: page || 0,
      sort: sort || '',
      size: size || 0,
    })
    .then((res) =>
      new Page<ApplicationVersion>().deserialize(res, ApplicationVersion),
    )
    .catch((e) => Promise.reject(e));
};

const show = (versionId: number) => {
  return api
    .get(nameSpace + '/' + versionId)
    .then((res) => new ApplicationVersion().deserialize(res))
    .catch((e) => Promise.reject(e));
};

const create = (payload) => {
  return api.post(nameSpace, payload);
};

const remove = (versionId: number) => {
  return api.remove(`${nameSpace}/${versionId}`);
};

const update = (versionId: number, payload) => {
  return api.put(`${nameSpace}/${versionId}`, payload);
};

const clone = (payload) => {
  return api.post(`${nameSpace}/clone`, payload);
};

const checkStatus = (applicationVersionId: number) => {
  return api.get(`${nameSpace}/status/${applicationVersionId}`);
};

const VersionService = {
  findAll,
  show,
  create,
  remove,
  update,
  clone,
  checkStatus,
};

export default VersionService;

export const useQueryShowVersion = ({
  versionId,
  enabled = true,
  onSuccess = (data: ApplicationVersion) => {},
}) => {
  const { getCachingTime } = useReactQueryCaching(VERSIONS_QUERY_KEY, false);

  return useQuery({
    queryKey: [VERSIONS_QUERY_KEY, versionId],
    queryFn: async () => await show(versionId),
    placeholderData: new ApplicationVersion(),
    staleTime: getCachingTime(),
    cacheTime: getCachingTime(),
    enabled,
    onSuccess,
  });
};

export const useVersionServiceFindAll = ({
  query,
  enabled = true,
  onError = console.error,
  onSuccess = (res) => {},
}) => {
  return useQuery({
    queryKey: [VERSIONS_QUERY_KEY, query],
    queryFn: async () => {
      const res = await api.get(nameSpace + '?query=' + query);
      return res as any;
    },
    enabled,
    onError,
    onSuccess,
  });
};

export const useFetchVersions = ({
  filterQuery = '',
  queryKey = [],
  keepPreviousData = true,
  enabled = true,
}) => {
  const { getCachingTime } = useReactQueryCaching(
    VERSIONS_INFINITE_QUERY_KEY,
    false,
  );

  return useInfiniteQuery({
    queryKey: [VERSIONS_INFINITE_QUERY_KEY, ...queryKey, filterQuery],
    queryFn: ({ pageParam = 0 }) => findAll(filterQuery, pageParam),
    getNextPageParam,
    staleTime: getCachingTime(),
    cacheTime: getCachingTime(),
    keepPreviousData,
    enabled,
  });
};

const useRefetchVersionsCache = () => {
  const queryClient = useQueryClient();
  return () => {
    queryClient.refetchQueries(VERSIONS_INFINITE_QUERY_KEY);
    queryClient.refetchQueries(VERSIONS_QUERY_KEY);
  };
};

export const useCreateVersionMutation = ({
  onSuccess = (data) => {},
  onError = console.error,
  onSettled = () => {},
}) => {
  const refetchAllVersionsCache = useRefetchVersionsCache();

  return useMutation(create, {
    onSuccess: (data) => {
      onSuccess(data);
      refetchAllVersionsCache();
    },
    onError,
    onSettled,
  });
};

export const useDeleteVersionMutation = ({
  onSuccess = () => {},
  onError = console.error,
  onSettled = () => {},
}) => {
  const refetchAllVersionsCache = useRefetchVersionsCache();

  return useMutation(remove, {
    onSuccess: () => {
      onSuccess();
      refetchAllVersionsCache();
    },
    onError,
    onSettled,
  });
};

export const useUpdateVersionMutation = ({
  onSuccess = (data) => {},
  onError = console.error,
  onSettled = () => {},
}) => {
  const refetchAllVersionsCache = useRefetchVersionsCache();

  return useMutation(
    ({ versionId, payload }) => {
      return update(versionId, payload);
    },
    {
      onSuccess: (data) => {
        onSuccess(data);
        refetchAllVersionsCache();
      },
      onError,
      onSettled,
    },
  );
};

export const useCloneVersionMutation = ({
  onSuccess = (data) => {},
  onError = console.error,
  onSettled = () => {},
}) => {
  const refetchAllVersionsCache = useRefetchVersionsCache();

  return useMutation(clone, {
    onSuccess: (data) => {
      onSuccess(data);
      refetchAllVersionsCache();
    },
    onError,
    onSettled,
  });
};
